<template>
  <ak-modal v-model:active="isModalActive">
    <ak-modal-body>
      <ak-modal-title>Clone Survey</ak-modal-title>
      <ak-form ref="cloneForm">
        <ak-field label="Survey Name" required>
          <ak-input
            ref="nameInput"
            v-model="name"
            type="text"
            required
            :custom-rules="customRules"
            @input="slugify"
          />
        </ak-field>
        <ak-field ref="slugField" label="Slug" required>
          <ak-input v-model="slug" type="text" required disabled />
        </ak-field>
      </ak-form>
    </ak-modal-body>
    <ak-modal-footer>
      <ak-modal-button @click.stop.prevent="isModalActive = false">
        Cancel
      </ak-modal-button>
      <ak-modal-button color="success" @click.stop.prevent="confirm">
        Clone
      </ak-modal-button>
    </ak-modal-footer>
  </ak-modal>
</template>

<script>
import { defineComponent, watch, ref, nextTick } from 'vue'
import Modal from '@akerolabs/akero-vue/src/components/modal/Modal.vue'
import ModalBody from '@akerolabs/akero-vue/src/components/modal/ModalBody.vue'
import ModalTitle from '@akerolabs/akero-vue/src/components/modal/ModalTitle.vue'
import ModalFooter from '@akerolabs/akero-vue/src/components/modal/ModalFooter.vue'
import ModalButton from '@akerolabs/akero-vue/src/components/modal/ModalButton.vue'
import Form from '@akerolabs/akero-vue/src/components/form/Form.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Input from '@akerolabs/akero-vue/src/components/input/Input.vue'
import { slugger } from '@akerolabs/akero-vue/src/utils/helpers'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import { useRouter } from '../../legacy/router'

export default defineComponent({
  name: 'AkCloneSurveyModal',
  components: {
    [Modal.name]: Modal,
    [ModalBody.name]: ModalBody,
    [ModalTitle.name]: ModalTitle,
    [ModalFooter.name]: ModalFooter,
    [ModalButton.name]: ModalButton,
    [Form.name]: Form,
    [Field.name]: Field,
    [Input.name]: Input,
  },
  props: {
    /**
     * @type {Boolean}
     * @description use to display and hide the modal
     */
    isActive: {
      type: Boolean,
      default: false,
    },
    /**
     * @type {String}
     * @description Account _id string
     */
    account: {
      type: String,
      required: true,
    },
    /**
     * @type {String}
     * @description Agency _id string
     */
    agency: {
      type: String,
      required: true,
    },

    /**
     * @description The survey object to clone
     * @type {Object}
     */
    survey: {
      type: Object,
      required: true,
    },
  },
  emits: ['update:isActive', 'clone'],
  setup(props, { emit }) {
    const router = useRouter()

    const isModalActive = ref(false)
    const name = ref('')
    const slug = ref('')
    const cloneForm = ref(null)
    const clone = ref({})
    const nameInput = ref(null)
    const slugField = ref(null)

    /**
     * @description Custom validation for a survey slug
     * @param {Node} input
     */
    const uniqueSlug = async (input) => {
      let message = undefined
      let isValid = true
      const slugCheck = await request.get('/survey', {
        params: {
          account: props.account,
          agency: props.agency,
          slug: slug.value,
        },
      })
      if (slugCheck.data.data.length > 0) {
        input.setCustomValidity(' ')
        message = 'The survey slug is already in use'
        isValid = false
      } else {
        input.setCustomValidity('')
        message = ''
        isValid = true
      }
      if (slug.value === '' || !slug.value) {
        input.setCustomValidity('')
        message = ''
        isValid = true
      }
      slugField.value.setMessage(message)
      return isValid
    }

    const customRules = [uniqueSlug]

    /**
     * @description Emits the update to the isActive prop and resets the generated list when modal is closed
     */
    watch(isModalActive, (value) => {
      emit('update:isActive', value)
      if (value) {
        nextTick(() => {
          nameInput.value.focus()
        })
      }
    })

    /**
     * @description Updates the internal isModalActive based on the isActive prop
     */
    watch(
      () => props.isActive,
      (value) => {
        isModalActive.value = value
      },
    )

    /**
     * @description watch the survey object so we can clear out any old data and set it up for cloning
     */
    watch(
      () => props.survey,
      (value) => {
        const temp = {
          clonedFrom: value._id,
          status: 'unpublished',
          current_completion_value: 0,
        }
        const exclude = [
          '_id',
          'id',
          'status',
          'clonedFrom',
          'current_completion_value',
          'updatedOn',
          'createdOn',
          'name',
          'slug',
        ]
        for (const key in value) {
          if (Object.prototype.hasOwnProperty.call(value, key)) {
            if (!exclude.includes(key)) {
              temp[key] = value[key]
            }
          }
        }
        clone.value = temp
      },
    )

    /**
     * @description convert a survey name to a slug
     */
    const slugify = () => {
      nextTick(async () => {
        slug.value = slugger(name.value)
      })
    }

    /**
     * @description make a clone
     */
    const confirm = async () => {
      const v = await cloneForm.value.validate(true)
      if (!v.some((item) => item === false)) {
        const create = { ...clone.value }
        create.name = name.value
        create.slug = slug.value
        const { data } = await request.post('/survey', create)
        router.push('editSurvey', { surveyId: data.data._id })
      }
    }

    return {
      isModalActive,
      confirm,
      name,
      slug,
      slugField,
      slugify,
      customRules,
      cloneForm,
      nameInput,
    }
  },
})
</script>
